import type { SxProps, Theme } from '@mui/material';

import { resolveJobTagIcon } from './icons';

interface JobTagIconProps {
  /** The `icon` key stored on the tag. Unknown keys fall back, never throw. */
  iconKey: string | null | undefined;
  fontSize?: 'inherit' | 'small' | 'medium' | 'large';
  sx?: SxProps<Theme>;
  /** Marks the icon decorative when an adjacent label already names the tag. */
  titleAccess?: string;
}

/**
 * Renders a job tag's icon from its stored key.
 *
 * Colour AND size are inherited by default, not set: tags carry no colour of
 * their own, and an icon dropped into a `Chip` or a header meta row has to
 * match the icons already there. Both containers size their icon slot with a
 * rule more specific than `SvgIcon`'s own `fontSize` class, so `inherit` lets
 * that rule win — passing `fontSize="small"` instead pins the icon to 20px
 * and it renders visibly larger than its neighbours.
 *
 * Pass an explicit `fontSize` or `sx.fontSize` only outside such a container,
 * where nothing else is setting the size.
 * @param root0
 * @param root0.iconKey
 * @param root0.fontSize
 * @param root0.sx
 * @param root0.titleAccess
 */
export function JobTagIcon({
  iconKey,
  fontSize = 'inherit',
  sx = undefined,
  titleAccess = undefined,
}: JobTagIconProps) {
  const Icon = resolveJobTagIcon(iconKey);
  return (
    <Icon
      fontSize={fontSize}
      titleAccess={titleAccess}
      sx={{ color: 'inherit', ...sx }}
    />
  );
}
